import { useEffect, useRef } from 'preact/hooks';
import type { Profile } from '../core/types';
import { fmtNum } from '../core/util';
import type { State } from '../state/store';
import { RAMPS, rampAt } from '../render/ramps';
import { INDICES } from '../render/indices';
import { anchors, canBalance, canLock, isClassifying } from '../render/style';
import { ClassLegend } from './Classes';

export function Legend({ state, profile }: { state: State; profile: Profile }) {
  const cv = useRef<HTMLCanvasElement>(null);
  const showRamp = canLock(state);
  const [rlo, rhi] = anchors(state);

  useEffect(() => {
    const c = cv.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    if (showRamp) {
      const stops = RAMPS[state.rampId]?.stops ?? RAMPS['rdylgn']!.stops;
      for (let x = 0; x < c.width; x++) {
        const [r, g, b] = rampAt(stops, x / (c.width - 1));
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(x, 0, 1, c.height);
      }
      // Shade what the window is filtering out, so it is obvious which colours are
      // anchored but hidden — the thing a normal legend cannot show you.
      if (state.lockRamp && state.rampLo != null && rhi > rlo) {
        const at = (v: number) => Math.round(Math.min(1, Math.max(0, (v - rlo) / (rhi - rlo))) * c.width);
        ctx.fillStyle = 'rgba(13,20,23,.78)';
        ctx.fillRect(0, 0, at(state.lo), c.height);
        ctx.fillRect(at(state.hi), 0, c.width - at(state.hi), c.height);
        ctx.fillStyle = 'rgba(242,169,59,.95)';
        ctx.fillRect(at(state.lo), 0, 1, c.height);
        ctx.fillRect(at(state.hi) - 1, 0, 1, c.height);
      }
    } else {
      ['#E0614B', '#79C4A2', '#5B8FD0'].forEach((col, i) => {
        ctx.fillStyle = col;
        ctx.fillRect(i * c.width / 3, 0, c.width / 3, c.height);
      });
    }
  });

  const title = state.mode === 'index'
    ? INDICES.find((i) => i.id === state.indexId)?.name ?? state.indexId
    : state.mode === 'single' ? `band ${state.singleBand + 1} · ${profile.bands[state.singleBand]?.role}`
      : state.mode === 'cir' ? 'false colour (CIR)'
        : state.mode === 'rgb' ? 'true colour' : 'custom composite';
  const balanced = canBalance(state) && state.balanceBands;
  const filtering = state.lockRamp && state.rampLo != null && (state.lo > rlo || state.hi < rhi);

  if (isClassifying(state)) {
    return (
      <div class="ov legend">
        <h3>Classes</h3>
        <ClassLegend classes={state.classes} />
      </div>
    );
  }
  return (
    <div class="ov legend">
      <h3>{title}</h3>
      <canvas ref={cv} width={200} height={11} />
      <div class="lb">
        <span>{balanced ? 'p2' : fmtNum(rlo)}</span>
        <span>{balanced ? 'p98' : fmtNum(rhi)}</span>
      </div>
      {filtering && <div class="lb win">showing {fmtNum(state.lo)} – {fmtNum(state.hi)}</div>}
    </div>
  );
}
